import React, { useEffect, useMemo, useRef, useState, Suspense } from "react";
import axios from "axios";
import Select from "react-select";
import MemberDetailsModal from "./MemberDetailsModal";
import "./MemberStatisticsModal.css";

// Lazy-load chart to speed initial UI
const ReactECharts = React.lazy(() => import("echarts-for-react"));

/* -------------------------- Debounce helper -------------------------- */
function useDebouncedValue(value, delay = 300) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(id);
    }, [value, delay]);
    return debounced;
}

/* ---------------------------- API wrapper ---------------------------- */
const api = {
    lodges: () => axios.get("/api/member_stats/lodges"),
    membershipStatuses: () => axios.get("/api/member_stats/membership_statuses"),
    membershipTypes: () => axios.get("/api/member_stats/membership_types"),
    stats: (params, signal) =>
        axios.get("/api/member_stats/statistics", { params, signal }),
    typesByPeriod: (params, signal) =>
        axios.get("/api/member_stats/membership_types_by_period", { params, signal }),
    statusesByPeriod: (params, signal) =>
        axios.get("/api/member_stats/membership_statuses_by_period", { params, signal }),
};

/* ============================ Main Modal Component ============================ */
function MemberStatisticsModal({ onClose }) {
    // lists / filters
    const [lodges, setLodges] = useState([]);
    const [membershipStatuses, setMembershipStatuses] = useState([]);
    const [membershipTypes, setMembershipTypes] = useState([]);

    const [selectedLodge, setSelectedLodge] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");
    const [selectedType, setSelectedType] = useState("");

    const [startDate, setStartDate] = useState("1880-01-01");
    const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
    const [granularity, setGranularity] = useState("year"); // "year" | "month"

    // data
    const [statistics, setStatistics] = useState([]); // paged rows
    const [membershipTypesByYear, setMembershipTypesByYear] = useState({});
    const [statusesByYear, setStatusesByYear] = useState({});

    // pagination
    const itemsPerPage = 10000;
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [totalMembers, setTotalMembers] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [maxDegrees, setMaxDegrees] = useState(0);

    // summaries
    const [numTypes, setNumTypes] = useState(0);
    const [numAffiliates, setNumAffiliates] = useState(0);
    const [numActiveMembers, setNumActiveMembers] = useState(0);
    const [numDeceased, setNumDeceased] = useState(0);
    const [numStruckOff, setNumStruckOff] = useState(0);
    const [numResigned, setNumResigned] = useState(0);
    const [avgStartAge, setAvgStartAge] = useState(0);
    const [avgEndAge, setAvgEndAge] = useState(0);

    // loading
    const [loadingTable, setLoadingTable] = useState(false);
    const [loadingTypes, setLoadingTypes] = useState(false);
    const [loadingStatuses, setLoadingStatuses] = useState(false);

    // view + chart
    const [viewMode, setViewMode] = useState("chart"); // 'chart' | 'table'
    const [chartKind, setChartKind] = useState("types");
    const [chartInstance, setChartInstance] = useState(null);
    const [editingMemberId, setEditingMemberId] = useState(null);

    // slider seeding + hard remount
    const [snapToFilters, setSnapToFilters] = useState(true);
    const [sliderVersion, setSliderVersion] = useState(0);
    const [chartMountKey, setChartMountKey] = useState(0);

    // abort controllers + request tokens
    const tableCtrlRef = useRef(null);
    const typesCtrlRef = useRef(null);
    const statusCtrlRef = useRef(null);
    const reqTokenRef = useRef(0); // increases each refresh wave

    // debounced filters
    const dLodge = useDebouncedValue(selectedLodge, 300);
    const dStatus = useDebouncedValue(selectedStatus, 300);
    const dType = useDebouncedValue(selectedType, 300);
    const dStart = useDebouncedValue(startDate, 300);
    const dEnd = useDebouncedValue(endDate, 300);
    const dGran = useDebouncedValue(granularity, 0);

    /* -------------------------- Modal accessibility -------------------------- */
    useEffect(() => {
        function onKey(e) { if (e.key === "Escape") onClose(); }
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    /* --------------------------- Init reference lists --------------------------- */
    useEffect(() => {
        (async () => {
            try {
                const [l, s, t] = await Promise.all([
                    api.lodges(), api.membershipStatuses(), api.membershipTypes()
                ]);
                setLodges(l.data.map(d => ({ value: d.id, label: d.name })));
                setMembershipStatuses(s.data.map(d => ({ value: d.id, label: d.name })));
                setMembershipTypes(t.data.map(d => ({ value: d.id, label: d.name })));
            } catch (e) {
                console.error("Init lists error:", e);
            }
        })();
    }, []);

    /* ------------------------- Fetch on debounced change ------------------------- */
    useEffect(() => {
        // start a new "wave" id
        reqTokenRef.current += 1;

        // Clear old UI immediately so nothing stale appears
        setStatistics([]);
        setMembershipTypesByYear({});
        setStatusesByYear({});
        setMaxDegrees(0);
        setHasMore(true);
        setTotalMembers(0);
        setTotalPages(0);

        // Fully remount chart & reseed slider
        setChartInstance((inst) => { try { inst?.dispose?.(); } catch {} return null; });
        setChartMountKey((k) => k + 1);
        setSliderVersion((v) => v + 1);

        // cancel in-flight requests
        tableCtrlRef.current?.abort?.();
        typesCtrlRef.current?.abort?.();
        statusCtrlRef.current?.abort?.();

        // fetch fresh
        setCurrentPage(1);
        fetchStatistics(1, true);
        fetchMembershipTypesByPeriod();
        fetchMembershipStatusesByPeriod();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dLodge, dStatus, dType, dStart, dEnd, dGran]);

    // Hard remount chart when switching chart type
    useEffect(() => {
        setChartInstance((inst) => { try { inst?.dispose?.(); } catch {} return null; });
        setChartMountKey((k) => k + 1);
        setSliderVersion((v) => v + 1);
    }, [chartKind]);

    /* ----------------------------------- Fetchers ----------------------------------- */
    // UUID-safe param handling
    const normParam = (v) => (v === "" || v == null ? undefined : String(v));
    const makeParams = (base) =>
        Object.fromEntries(Object.entries(base).filter(([, v]) => v !== undefined));

    const fetchStatistics = async (page, reset = false) => {
        tableCtrlRef.current?.abort?.();
        tableCtrlRef.current = new AbortController();
        setLoadingTable(true);

        // token for this request
        const myToken = reqTokenRef.current;

        try {
            const params = makeParams({
                lodge_id: normParam(dLodge),
                status_id: normParam(dStatus),
                type_id:   normParam(dType),
                start_date: dStart || undefined,
                end_date:   dEnd   || undefined,
                limit: itemsPerPage,
                offset: (page - 1) * itemsPerPage,
            });

            const res = await api.stats(params, tableCtrlRef.current.signal);
            if (myToken !== reqTokenRef.current) return; // stale

            const newStats = res.data.tableData || [];
            setMaxDegrees(prev => Math.max(prev, ...newStats.map(s => s.degrees?.length || 0)));
            if (reset) setStatistics(newStats);
            else setStatistics(prev => [...prev, ...newStats]);

            const total = res.data.total ?? newStats.length;
            setTotalMembers(total);
            const pages = Math.ceil(total / itemsPerPage);
            setTotalPages(pages);
            setHasMore(page < pages);
        } catch (e) {
            if (!["CanceledError", "ERR_CANCELED"].includes(e.name || e.code)) {
                console.error("Statistics fetch error", e);
            }
        } finally {
            if (myToken === reqTokenRef.current) setLoadingTable(false);
        }
    };

    const fetchMembershipTypesByPeriod = async () => {
        typesCtrlRef.current?.abort?.();
        typesCtrlRef.current = new AbortController();
        setLoadingTypes(true);
        const myToken = reqTokenRef.current;

        try {
            const params = makeParams({
                lodge_id: normParam(dLodge),
                status_id: normParam(dStatus),
                type_id:   normParam(dType),
                start_date: dStart || undefined,
                end_date:   dEnd   || undefined,
                interval:   dGran,
            });
            const res = await api.typesByPeriod(params, typesCtrlRef.current.signal);
            if (myToken !== reqTokenRef.current) return; // stale
            setMembershipTypesByYear(res.data || {});
        } catch (e) {
            if (!["CanceledError", "ERR_CANCELED"].includes(e.name || e.code)) {
                console.error("Types by period error", e);
            }
        } finally {
            if (myToken === reqTokenRef.current) setLoadingTypes(false);
        }
    };

    // ---- Canonicalization helpers for statuses ----
    const canonicalizeStatus = (raw) => {
        if (!raw) return "Unknown";
        const s = String(raw).trim().toLowerCase();
        if (["deceased", "died", "death"].includes(s)) return "Deceased";
        if (["struck off", "struck-off", "struckoff", "removed"].includes(s)) return "Struck Off";
        if (["resigned", "resignation"].includes(s)) return "Resigned";
        if (["active", "current"].includes(s)) return "Active";
        return raw; // fallback
    };

    const foldStatusesToCanonical = (byPeriod) => {
        const out = {};
        for (const [period, counts] of Object.entries(byPeriod || {})) {
            const canon = {};
            for (const [label, value] of Object.entries(counts || {})) {
                const k = canonicalizeStatus(label);
                canon[k] = (canon[k] || 0) + (value || 0);
            }
            out[period] = canon;
        }
        return out;
    };

    const fetchMembershipStatusesByPeriod = async () => {
        statusCtrlRef.current?.abort?.();
        statusCtrlRef.current = new AbortController();
        setLoadingStatuses(true);
        const myToken = reqTokenRef.current;

        try {
            const params = makeParams({
                lodge_id: normParam(dLodge),
                // NOTE: intentionally not filtering by selectedStatus here,
                // so the overlay shows all statuses for context.
                type_id:  normParam(dType),
                start_date: dStart || undefined,
                end_date:   dEnd   || undefined,
                interval:   dGran,
            });
            const res = await api.statusesByPeriod(params, statusCtrlRef.current.signal);
            if (myToken !== reqTokenRef.current) return; // stale
            setStatusesByYear(foldStatusesToCanonical(res.data || {}));
        } catch (e) {
            if (!["CanceledError", "ERR_CANCELED"].includes(e.name || e.code)) {
                console.error("Statuses by period error", e);
            }
        } finally {
            if (myToken === reqTokenRef.current) setLoadingStatuses(false);
        }
    };

    /* -------------------------------- Derive summaries ------------------------------- */
    useEffect(() => {
        if (!statistics.length) {
            setNumTypes(0);
            setNumAffiliates(0);
            setNumActiveMembers(0);
            setNumDeceased(0);
            setNumStruckOff(0);
            setNumResigned(0);
            setAvgStartAge(0);
            setAvgEndAge(0);
            return;
        }

        const canon = (s) => canonicalizeStatus(s.status_name);

        const active = statistics.filter(s => canon(s) === "Active").length;
        const deceased = statistics.filter(s => canon(s) === "Deceased").length;
        const struck = statistics.filter(s => canon(s) === "Struck Off").length;
        const resigned = statistics.filter(s => canon(s) === "Resigned").length;

        const affiliates = statistics.filter(s => s.type_name === "Affiliate").length;
        const startAges = statistics.map(s => s.age_at_start).filter(a => a != null);
        const endAges   = statistics.map(s => s.age_at_end).filter(a => a != null);

        setNumActiveMembers(active);
        setNumDeceased(deceased);
        setNumStruckOff(struck);
        setNumResigned(resigned);
        setNumAffiliates(affiliates);
        setAvgStartAge(startAges.length ? (startAges.reduce((a,b)=>a+b,0)/startAges.length).toFixed(1) : 0);
        setAvgEndAge(endAges.length ? (endAges.reduce((a,b)=>a+b,0)/endAges.length).toFixed(1) : 0);
        setNumTypes(new Set(statistics.map(s => s.type_name)).size);
    }, [statistics]);

    /* --------------------------------- Utilities --------------------------------- */
    const formatDateToSAST = (date) => {
        if (!date) return "Present";
        const options = { timeZone: "Africa/Johannesburg", year: "numeric", month: "2-digit", day: "2-digit" };
        return new Date(date).toLocaleDateString("en-ZA", options);
    };
    const csvEscape = (s) => {
        if (s == null) return "";
        const str = String(s);
        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };

    // sums & period helpers
    const sumObjectValues = (obj) => Object.values(obj || {}).reduce((a, b) => a + (b || 0), 0);

    // only keep periods with any > 0 counts from types OR statuses
    const getActivePeriods = (typesByPeriod, statusesByPeriod) => {
        const all = new Set([
            ...Object.keys(typesByPeriod || {}),
            ...Object.keys(statusesByPeriod || {}),
        ]);
        const active = [];
        for (const p of all) {
            const hasTypes = sumObjectValues(typesByPeriod?.[p]) > 0;
            const hasStatuses = sumObjectValues(statusesByPeriod?.[p]) > 0;
            if (hasTypes || hasStatuses) active.push(p);
        }
        return active.sort();
    };

    // clamp filter window to data window (intersection)
    const clampToDataWindow = (minFilter, maxFilter, periodsArr) => {
        if (!periodsArr.length) return [minFilter, maxFilter];
        const first = periodsArr[0];
        const last  = periodsArr[periodsArr.length - 1];
        const minV = minFilter && minFilter > first ? minFilter : first;
        const maxV = maxFilter && maxFilter < last  ? maxFilter : last;
        return [minV, maxV];
    };

    // convert "YYYY" or "YYYY-MM" to ISO range
    const getDataDateWindow = (periodsArr) => {
        if (!periodsArr.length) return [dStart, dEnd];
        const first = periodsArr[0];
        const last  = periodsArr[periodsArr.length - 1];
        const toISO = (p, end=false) => {
            if (/^\d{4}$/.test(p)) return end ? `${p}-12-31` : `${p}-01-01`;
            const [y,m] = p.split("-");
            const mm = Number(m);
            const lastDay = new Date(Number(y), mm, 0).getDate();
            return end ? `${y}-${m}-${String(lastDay).padStart(2,"0")}` : `${y}-${m}-01`;
        };
        return [toISO(first, false), toISO(last, true)];
    };

    // colors
    const BAR_COLORS = ["#5470C6", "#91CC75", "#EF80A5", "#FAC858", "#73C0DE", "#3BA272", "#9A60B4", "#EA7CCC"];
    const typeColorMap = useMemo(() => {
        const map = new Map();
        membershipTypes.forEach((t, i) => map.set(t.value, BAR_COLORS[i % BAR_COLORS.length]));
        return map;
    }, [membershipTypes]);

    // dynamic palette for status overlays (lines/areas)
    const STATUS_PALETTE = [
        "rgba(183, 28, 28, 0.30)",   // red-ish
        "rgba(0, 0, 0, 0.30)",       // grey/black
        "rgba(123, 31, 162, 0.30)",  // purple
        "rgba(33, 150, 243, 0.30)",  // blue
        "rgba(255, 152, 0, 0.30)",   // orange
    ];

    const desiredTypeOrder = [
        "Founding Member","Full Member","Affiliate Full Member","Affiliate","Emeritus Member","Honorary Member"
    ].filter(lbl => membershipTypes.find(t => t.label === lbl));

    const lodgeRankings = useMemo(() => {
        const map = new Map();
        statistics.forEach((row) => {
            const lodge = row.lodge_name || "Unknown";
            const entry = map.get(lodge) || { lodge, total: 0, active: 0, deceased: 0, resigned: 0, struck: 0 };
            entry.total += 1;
            const status = canonicalizeStatus(row.status_name);
            if (status === "Active") entry.active += 1;
            if (status === "Deceased") entry.deceased += 1;
            if (status === "Resigned") entry.resigned += 1;
            if (status === "Struck Off") entry.struck += 1;
            map.set(lodge, entry);
        });
        return [...map.values()].sort((a, b) => b.total - a.total || a.lodge.localeCompare(b.lodge));
    }, [statistics]);

    const typeRankings = useMemo(() => {
        const map = new Map();
        statistics.forEach((row) => {
            const type = row.type_name || "Unknown";
            map.set(type, (map.get(type) || 0) + 1);
        });
        return [...map.entries()].map(([type, total]) => ({ type, total })).sort((a, b) => b.total - a.total || a.type.localeCompare(b.type));
    }, [statistics]);

    const occupationRankings = useMemo(() => {
        const map = new Map();
        statistics.forEach((row) => {
            const occupation = row.occupation?.trim() || "Not Provided";
            map.set(occupation, (map.get(occupation) || 0) + 1);
        });
        return [...map.entries()].map(([occupation, total]) => ({ occupation, total })).sort((a, b) => b.total - a.total || a.occupation.localeCompare(b.occupation)).slice(0, 12);
    }, [statistics]);

    const memberGaps = useMemo(() => statistics
        .map((row) => ({
            ...row,
            missing: [
                !row.birthdate && "birthdate",
                !row.occupation || row.occupation === "Not Provided" ? "occupation" : null,
                !row.start_date && "start date",
                !(row.degrees || []).length && "degrees",
                !row.lodge_name && "lodge",
            ].filter(Boolean),
        }))
        .filter((row) => row.missing.length)
        .sort((a, b) => b.missing.length - a.missing.length || `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)), [statistics]);

    const ageBandRows = useMemo(() => {
        const bands = [
            { label: "Under 30", min: 0, max: 29 },
            { label: "30-39", min: 30, max: 39 },
            { label: "40-49", min: 40, max: 49 },
            { label: "50-59", min: 50, max: 59 },
            { label: "60+", min: 60, max: 130 },
            { label: "Unknown", min: null, max: null },
        ];
        return bands.map((band) => ({
            label: band.label,
            total: statistics.filter((row) => {
                if (row.age_at_start == null) return band.label === "Unknown";
                return row.age_at_start >= band.min && row.age_at_start <= band.max;
            }).length,
        }));
    }, [statistics]);

    const exportGapsCSV = () => {
        if (!memberGaps.length) { alert("No data gaps for the current filters."); return; }
        const headers = ["Name", "Lodge", "Status", "Type", "Missing"];
        const rows = memberGaps.map((row) => [
            `${row.first_name} ${row.last_name}`,
            row.lodge_name || "Unknown",
            row.status_name || "",
            row.type_name || "",
            row.missing.join(", "),
        ]);
        const csv = [headers, ...rows].map(r => r.map(csvEscape).join(",")).join("\n");
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `membership_data_gaps_${dStart}_to_${dEnd}.csv`;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
    };

    // helpers
    const periodKey = (d) => {
        const dt = new Date(d);
        return dGran === "year" ? String(dt.getFullYear())
            : `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}`;
    };
    const filteredMin = useMemo(() => {
        const dt = new Date(dStart);
        return dGran === "year" ? String(dt.getFullYear())
            : `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}`;
    }, [dStart, dGran]);
    const filteredMax = useMemo(() => {
        const dt = new Date(dEnd);
        return dGran === "year" ? String(dt.getFullYear())
            : `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}`;
    }, [dEnd, dGran]);

    // category axis dataZoom config
    const categoryZoom = (periodsArr) => {
        if (!periodsArr.length) return [{ type: "slider", filterMode: "filter", start: 0, end: 100 }];
        const [seedMin, seedMax] = clampToDataWindow(filteredMin, filteredMax, periodsArr);
        return [
            snapToFilters
                ? { type: "slider", filterMode: "filter", startValue: seedMin, endValue: seedMax }
                : { type: "slider", filterMode: "filter", start: 0, end: 100 }
        ];
    };

    /* ----------------------------- Chart options ----------------------------- */
    // 1) Membership Type Mix + Status overlays (stacked bars + lines)
    const buildTypesStatusesOption = () => {
        const activePeriods = getActivePeriods(membershipTypesByYear, statusesByYear);
        if (!activePeriods.length) return null;

        const barSeries = desiredTypeOrder.map(lbl => {
            const t = membershipTypes.find(x => x.label === lbl);
            const color = t ? typeColorMap.get(t.value) : undefined;
            return {
                name: lbl,
                type: "bar",
                stack: "TypeTotal",
                itemStyle: { color },
                emphasis: { focus: "series" },
                data: activePeriods.map(p => membershipTypesByYear?.[p]?.[lbl] ?? 0)
            };
        });

        // build dynamic status list from payload (optionally exclude "Active")
        const statusSet = new Set();
        for (const p of activePeriods) {
            const row = statusesByYear?.[p] || {};
            for (const k of Object.keys(row)) statusSet.add(k);
        }
        // If you only want to overlay exit statuses:
        // const dynamicStatusNames = [...statusSet].filter(s => s !== "Active");
        const dynamicStatusNames = [...statusSet];

        const lineSeries = dynamicStatusNames.map((st, i) => {
            const base = STATUS_PALETTE[i % STATUS_PALETTE.length];
            const outline = base.replace(/0\.\d+\)$/, '1)');
            return {
                name: st,
                type: "line",
                symbol: "none",
                lineStyle: { width: 2, color: outline },
                areaStyle: { color: base },
                data: activePeriods.map(p => statusesByYear?.[p]?.[st] ?? 0),
            };
        });

        const [seedMin, seedMax] = clampToDataWindow(filteredMin, filteredMax, activePeriods);

        return {
            tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
            legend: { type: "scroll" },
            grid: { left: "3%", right: "4%", bottom: "12%", containLabel: true },
            xAxis: {
                type: "category",
                data: activePeriods,
                min: seedMin,
                max: seedMax,
                axisLabel: { rotate: 45, interval: 0 },
                boundaryGap: ["1%","1%"]
            },
            yAxis: { type: "value", name: "Members" },
            series: [...barSeries, ...lineSeries],
            dataZoom: categoryZoom(activePeriods),
        };
    };

    // 2) Age at joining vs date joined (scatter)
    const buildAgeScatterOption = () => {
        const points = statistics
            .filter(s => s.age_at_start != null && s.start_date)
            .map(s => [new Date(s.start_date).getTime(), s.age_at_start, `${s.first_name} ${s.last_name}`]);
        if (!points.length) return null;
        return {
            tooltip: { trigger: "item", formatter: p => {
                    const dt = new Date(p.value[0]).toISOString().slice(0,10);
                    return `${p.value[2]}<br/>Joined: ${dt}<br/>Age: ${p.value[1]}`;
                }},
            grid: { left: "3%", right: "4%", bottom: "12%", containLabel: true },
            xAxis: { type: "time", name: "Date Joined", min: +new Date(dStart), max: +new Date(dEnd), axisLabel: { rotate: 45 } },
            yAxis: { type: "value", name: "Age at Joining" },
            series: [{ type: "scatter", name: "Member", data: points, symbolSize: 6 }],
            dataZoom: [
                snapToFilters
                    ? { type: "slider", xAxisIndex: 0, filterMode: "filter", startValue: +new Date(dStart), endValue: +new Date(dEnd) }
                    : { type: "slider", xAxisIndex: 0, filterMode: "filter" },
                { type: "slider", yAxisIndex: 0 },
            ]
        };
    };

    // 3) Joins vs Exits per period (stacked)
    const buildFlowBarsOption = () => {
        if (!statistics.length) return null;
        const map = new Map();
        const add = (k, t) => { const o = map.get(k) || { joins:0, exits:0 }; o[t]++; map.set(k,o); };
        statistics.forEach(s => {
            if (s.start_date) add(periodKey(s.start_date), "joins");
            if (s.end_date) add(periodKey(s.end_date), "exits");
        });
        const keys = Array.from(map.keys()).sort();
        if (!keys.length) return null;

        const [seedMin, seedMax] = clampToDataWindow(filteredMin, filteredMax, keys);

        return {
            tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
            legend: {},
            grid: { left: "3%", right: "4%", bottom: "12%", containLabel: true },
            xAxis: { type: "category", data: keys, min: seedMin, max: seedMax, axisLabel: { rotate: 45 } },
            yAxis: { type: "value", name: "Count" },
            series: [
                { name: "Joins", type: "bar", stack: "flow", data: keys.map(k => map.get(k).joins) },
                { name: "Exits", type: "bar", stack: "flow", data: keys.map(k => map.get(k).exits) },
            ],
            dataZoom: categoryZoom(keys),
        };
    };

    // 4) Active members over time (line; approximate from loaded rows)
    const buildActiveLineOption = () => {
        if (!statistics.length) return null;
        const events = new Map();
        const add = (k, d) => events.set(k, (events.get(k) || 0) + d);
        statistics.forEach(s => {
            if (s.start_date) add(periodKey(s.start_date), +1);
            if (s.end_date) add(periodKey(s.end_date), -1);
        });
        const keys = Array.from(events.keys()).sort();
        if (!keys.length) return null;

        const [seedMin, seedMax] = clampToDataWindow(filteredMin, filteredMax, keys);

        let acc = 0;
        const data = keys.map(k => (acc += events.get(k)));
        return {
            tooltip: { trigger: "axis" },
            grid: { left: "3%", right: "4%", bottom: "12%", containLabel: true },
            xAxis: { type: "category", data: keys, min: seedMin, max: seedMax, axisLabel: { rotate: 45 } },
            yAxis: { type: "value", name: "Active (approx.)" },
            series: [{ name: "Active", type: "line", data }],
            dataZoom: categoryZoom(keys),
        };
    };

    // 5) Age-at-joining histogram (bins)
    const buildAgeHistOption = (binSize = 5) => {
        const ages = statistics.map(s => s.age_at_start).filter(a => a != null);
        if (!ages.length) return null;
        const min = Math.min(...ages), max = Math.max(...ages);
        const bins = [];
        for (let b = Math.floor(min/binSize)*binSize; b <= Math.ceil(max/binSize)*binSize; b += binSize) {
            bins.push({ start:b, end:b+binSize, label:`${b}-${b+binSize-1}`, count:0 });
        }
        ages.forEach(a => {
            const idx = Math.floor((a - bins[0].start) / binSize);
            const i = Math.max(0, Math.min(bins.length - 1, idx));
            bins[i].count += 1;
        });
        return {
            tooltip: { trigger: "axis" },
            grid: { left: "3%", right: "4%", bottom: "12%", containLabel: true },
            xAxis: { type: "category", data: bins.map(b => b.label), name: `Age bins (size=${binSize})`, axisLabel: { rotate: 45 } },
            yAxis: { type: "value", name: "Members" },
            series: [{ name: "Members", type: "bar", data: bins.map(b => b.count) }],
            dataZoom: [{ type: "slider", filterMode: "filter", start: 0, end: 100 }],
        };
    };

    // 6) Degree frequency over time (stacked by degree name)
    const buildDegreesStackOption = () => {
        const agg = new Map(); // period -> { degreeName -> count }
        const totalsByDegree = new Map();
        for (const row of statistics) {
            const arr = row.degrees || [];
            for (const d of arr) {
                if (!d?.date_obtained || !d?.degree_name) continue;
                const p = periodKey(d.date_obtained);
                const o = agg.get(p) || {};
                o[d.degree_name] = (o[d.degree_name] || 0) + 1;
                agg.set(p, o);
                totalsByDegree.set(d.degree_name, (totalsByDegree.get(d.degree_name) || 0) + 1);
            }
        }
        const periodsArr = Array.from(agg.keys()).sort();
        if (!periodsArr.length) return null;

        const [seedMin, seedMax] = clampToDataWindow(filteredMin, filteredMax, periodsArr);

        const degreeNames = Array.from(totalsByDegree.entries())
            .sort((a,b) => b[1]-a[1])
            .map(([name]) => name);

        const series = degreeNames.map((name, i) => ({
            name, type: "bar", stack: "degrees",
            itemStyle: { color: BAR_COLORS[i % BAR_COLORS.length] },
            emphasis: { focus: "series" },
            data: periodsArr.map(p => (agg.get(p)?.[name] ?? 0)),
        }));

        return {
            tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
            legend: { type: "scroll" },
            grid: { left: "3%", right: "4%", bottom: "12%", containLabel: true },
            xAxis: { type: "category", data: periodsArr, min: seedMin, max: seedMax, axisLabel: { rotate: 45 } },
            yAxis: { type: "value", name: "Degrees conferred" },
            series,
            dataZoom: categoryZoom(periodsArr),
        };
    };

    // 7) Occupation trends over time (stacked, top N + Other)
    const buildOccupationTrendsOption = () => {
        const agg = new Map(); // period -> { occupation -> count }
        const totalsByOcc = new Map();

        for (const s of statistics) {
            const occ = s.occupation?.trim() || "Unknown";
            const p = s.start_date ? periodKey(s.start_date) : null;
            if (!p) continue;
            const o = agg.get(p) || {};
            o[occ] = (o[occ] || 0) + 1;
            agg.set(p, o);
            totalsByOcc.set(occ, (totalsByOcc.get(occ) || 0) + 1);
        }

        const periodsArr = Array.from(agg.keys()).sort();
        if (!periodsArr.length) return null;

        const [seedMin, seedMax] = clampToDataWindow(filteredMin, filteredMax, periodsArr);

        const TOP_N = 8;
        const ranked = Array.from(totalsByOcc.entries()).sort((a,b)=>b[1]-a[1]);
        const topNames = ranked.slice(0, TOP_N).map(([name]) => name);
        const isTop = new Set(topNames);

        const series = topNames.map((name, i) => ({
            name, type: "bar", stack: "occ", itemStyle: { color: BAR_COLORS[i % BAR_COLORS.length] },
            emphasis: { focus: "series" },
            data: periodsArr.map(p => (agg.get(p)?.[name] ?? 0)),
        }));

        const otherData = periodsArr.map(p => {
            const row = agg.get(p) || {};
            let sumTop = 0, sumAll = 0;
            for (const [k,v] of Object.entries(row)) {
                sumAll += v; if (isTop.has(k)) sumTop += v;
            }
            return Math.max(0, sumAll - sumTop);
        });
        series.push({
            name: "Other",
            type: "bar",
            stack: "occ",
            itemStyle: { color: "#CCCCCC" },
            emphasis: { focus: "series" },
            data: otherData
        });

        return {
            tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
            legend: { type: "scroll" },
            grid: { left: "3%", right: "4%", bottom: "12%", containLabel: true },
            xAxis: { type: "category", data: periodsArr, min: seedMin, max: seedMax, axisLabel: { rotate: 45 } },
            yAxis: { type: "value", name: "Members (by occupation)" },
            series,
            dataZoom: categoryZoom(periodsArr),
        };
    };

    const currentOption = useMemo(() => {
        if (chartKind === "types") return buildTypesStatusesOption();
        if (chartKind === "ageScatter") return buildAgeScatterOption();
        if (chartKind === "flowBars") return buildFlowBarsOption();
        if (chartKind === "activeLine") return buildActiveLineOption();
        if (chartKind === "ageHist") return buildAgeHistOption();
        if (chartKind === "degreesStack") return buildDegreesStackOption();
        if (chartKind === "occupationTrends") return buildOccupationTrendsOption();
        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        chartKind, membershipTypesByYear, statusesByYear, statistics,
        filteredMin, filteredMax, dStart, dEnd, dGran, snapToFilters
    ]);

    // Resize on window change
    useEffect(() => {
        const onResize = () => chartInstance?.resize?.();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [chartInstance]);

    /* -------------------------------- CSV exports -------------------------------- */
    const exportTypesByPeriodCSV = () => {
        if (!Object.keys(membershipTypesByYear).length) { alert("No data available to export."); return; }
        const headers = ["Period", ...membershipTypes.map(t => t.label)];
        const rows = Object.entries(membershipTypesByYear).map(([period, counts]) => [
            period, ...membershipTypes.map(t => counts[t.label] || 0)
        ]);
        const csv = [headers, ...rows].map(r => r.map(csvEscape).join(",")).join("\n");
        const BOM = "\uFEFF";
        const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `membership_types_by_${granularity}.csv`;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
    };

    const exportMainCSV = () => {
        if (!statistics.length) { alert("No data available to export."); return; }
        const headers = [
            "Name","Lodge","Occupation","Birthdate","Age at Start","Age at End",
            "Status","Type","Start Date","End Date",
            ...Array.from({ length: maxDegrees }, (_, i) => `Degree ${i + 1}`)
        ];
        const rows = statistics.map(stat => {
            const degrees = Array.from({ length: maxDegrees }, (_, i) => {
                const d = stat.degrees?.[i];
                return d ? `${d.degree_name} (${formatDateToSAST(d.date_obtained)})` : "N/A";
            });
            return [
                `${stat.first_name} ${stat.last_name}`,
                stat.lodge_name || "Unknown",
                stat.occupation || "Not Provided",
                stat.birthdate ? formatDateToSAST(stat.birthdate) : "Unknown",
                stat.age_at_start ?? "N/A",
                stat.age_at_end ?? "N/A",
                stat.status_name,
                stat.type_name,
                formatDateToSAST(stat.start_date),
                formatDateToSAST(stat.end_date),
                ...degrees
            ];
        });
        const csv = [headers, ...rows].map(r => r.map(csvEscape).join(",")).join("\n");
        const BOM = "\uFEFF";
        const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
        const filename = [
            "membership_statistics",
            dLodge ? `lodge-${dLodge}` : "all-lodges",
            dStatus ? `status-${dStatus}` : "all-statuses",
            dType ? `type-${dType}` : "all-types",
            `${dStart}_to_${dEnd}`
        ].join("_") + ".csv";
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
    };

    // Save chart as PNG
    const saveChartAsImage = () => {
        if (!chartInstance) return;
        const url = chartInstance.getDataURL({ type: "png", pixelRatio: 2, backgroundColor: "#fff" });
        const a = document.createElement("a");
        a.href = url; a.download = `chart_${chartKind}_${granularity}_${dStart}_${dEnd}.png`; a.click();
    };

    /* ---------------------------- Table helpers ---------------------------- */
    const renderDegreeColumns = (degrees) => {
        const cells = [];
        for (let i = 0; i < maxDegrees; i++) {
            const d = degrees?.[i];
            cells.push(<td key={i}>{d ? `${d.degree_name} (${formatDateToSAST(d.date_obtained)})` : "N/A"}</td>);
        }
        return cells;
    };

    /* ------------------------------ Pagination ------------------------------ */
    const loadMore = () => {
        if (!hasMore || loadingTable) return;
        fetchStatistics(currentPage + 1);
        setCurrentPage(p => p + 1);
    };

    /* ---------------------------------- UI ---------------------------------- */
    return (
        <div
            className="member-stats-modal-overlay"
            aria-modal="true"
            role="dialog"
            aria-labelledby="member-stats-title"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="member-stats-modal-content" role="document">
                <div className="member-stats-modal-header">
                    <h2 id="member-stats-title">Membership Statistics</h2>
                    <button className="member-stats-modal-close-button" aria-label="Close dialog" onClick={onClose}>×</button>
                </div>

                <div className="member-stats-modal-body">
                    {/* Filters */}
                    <div className="member-stats-modal-filters">
                        <Select
                            options={[{ value: "", label: "All Lodges" }, ...lodges]}
                            value={lodges.find(l => String(l.value) === String(selectedLodge)) || { value: "", label: "All Lodges" }}
                            onChange={(o) => setSelectedLodge(o.value)}
                            placeholder="Filter by Lodge"
                            styles={{ control: (b)=>({ ...b, minWidth:"200px" }), menu:(b)=>({ ...b, zIndex:9999 }) }}
                        />
                        <Select
                            options={[{ value: "", label: "All Statuses" }, ...membershipStatuses]}
                            value={membershipStatuses.find(s => String(s.value) === String(selectedStatus)) || { value: "", label: "All Statuses" }}
                            onChange={(o) => setSelectedStatus(o.value)}
                            placeholder="Filter by Membership Status"
                            styles={{ control: (b)=>({ ...b, minWidth:"200px" }), menu:(b)=>({ ...b, zIndex:9999 }) }}
                        />
                        <Select
                            options={[{ value: "", label: "All Types" }, ...membershipTypes]}
                            value={membershipTypes.find(t => String(t.value) === String(selectedType)) || { value: "", label: "All Types" }}
                            onChange={(o) => setSelectedType(o.value)}
                            placeholder="Filter by Membership Type"
                            styles={{ control: (b)=>({ ...b, minWidth:"200px" }), menu:(b)=>({ ...b, zIndex:9999 }) }}
                        />
                        <input type="date" value={startDate} onChange={(e)=>setStartDate(e.target.value)} className="member-stats-modal-date-input" />
                        <input type="date" value={endDate}   onChange={(e)=>setEndDate(e.target.value)}   className="member-stats-modal-date-input" />
                        <select value={granularity} onChange={(e)=>setGranularity(e.target.value)} className="member-stats-modal-granularity-select">
                            <option value="year">Yearly</option>
                            <option value="month">Monthly</option>
                        </select>

                        {/* View toggle */}
                        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <button onClick={() => setViewMode(v => v === "chart" ? "table" : "chart")}>
                                {viewMode === "chart" ? "Show Table" : "Show Chart"}
                            </button>
                            <button onClick={exportGapsCSV}>Export Gaps CSV</button>
                        </div>
                    </div>

                    {/* Summary */}
                    <div className="member-stats-modal-summary">
                        <section className="mstat-kpi-grid">
                            <Kpi label="Members" value={totalMembers || statistics.length} />
                            <Kpi label="Active" value={numActiveMembers} />
                            <Kpi label="Deceased" value={numDeceased} />
                            <Kpi label="Resigned" value={numResigned} />
                            <Kpi label="Struck Off" value={numStruckOff} />
                            <Kpi label="Types" value={numTypes} />
                            <Kpi label="Affiliates" value={numAffiliates} />
                            <Kpi label="Avg Start Age" value={`${avgStartAge} yrs`} />
                            <Kpi label="Avg End Age" value={`${avgEndAge} yrs`} />
                        </section>

                        <section className="mstat-grid-three">
                            <Panel title="Membership Types" action={<span>{typeRankings.length} types</span>}>
                                <StatsTable
                                    headers={["Type", "Members"]}
                                    rows={typeRankings.map((row) => [row.type, row.total])}
                                    empty="No type data."
                                />
                            </Panel>
                            <Panel title="Age at Joining" action={<span>{avgStartAge} yrs average</span>}>
                                <StatsTable
                                    headers={["Age Band", "Members"]}
                                    rows={ageBandRows.map((row) => [row.label, row.total])}
                                    empty="No age data."
                                />
                            </Panel>
                            <Panel title="Top Occupations" action={<span>{occupationRankings.length} shown</span>}>
                                <StatsTable
                                    headers={["Occupation", "Members"]}
                                    rows={occupationRankings.map((row) => [row.occupation, row.total])}
                                    empty="No occupation data."
                                />
                            </Panel>
                        </section>

                        {/* Chart or Table */}
                        {viewMode === "chart" ? (
                            <Panel title="Membership Movement and Composition">
                            <div className="member-stats-modal-membership-types">
                                <div className="member-stats-modal-membership-types-header" style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                                    <select
                                        value={chartKind}
                                        onChange={(e)=> setChartKind(e.target.value)}
                                        className="member-stats-modal-chartkind-select"
                                        title="Choose a chart"
                                    >
                                        <option value="types">Membership Type Mix (stacked)</option>
                                        <option value="ageScatter">Age at Joining vs Date Joined</option>
                                        <option value="flowBars">Joins vs Exits per {granularity}</option>
                                        <option value="activeLine">Active Members Over Time</option>
                                        <option value="ageHist">Age at Joining — Histogram</option>
                                        <option value="degreesStack">Degree Frequency Over Time (stacked)</option>
                                        <option value="occupationTrends">Occupation Trends Over Time (stacked)</option>
                                    </select>

                                    {/* auto-fit + reset slider + fit filters */}
                                    <label style={{display:"inline-flex", alignItems:"center", gap:6}}>
                                        <input
                                            type="checkbox"
                                            checked={snapToFilters}
                                            onChange={(e) => { setSnapToFilters(e.target.checked); setSliderVersion(v => v + 1); }}
                                        />
                                        Auto-fit to date filters
                                    </label>
                                    <button onClick={() => setSliderVersion(v => v + 1)} title="Reset the chart slider to match the Start/End filters">
                                        Reset slider
                                    </button>
                                    <button
                                        onClick={() => {
                                            const activePeriods = getActivePeriods(membershipTypesByYear, statusesByYear);
                                            const [s, e] = getDataDateWindow(activePeriods);
                                            setStartDate(s); setEndDate(e);
                                        }}
                                        title="Set Start/End inputs to the current data window"
                                    >
                                        Fit filters to data
                                    </button>

                                    <button onClick={saveChartAsImage}>Save Chart</button>
                                    <button onClick={exportTypesByPeriodCSV}>Save Period CSV</button>
                                    <button onClick={exportMainCSV}>Export Members CSV</button>
                                </div>

                                <div className="member-stats-modal-chart-container">
                                    <Suspense fallback={<div style={{padding:"1rem"}}>Loading chart…</div>}>
                                        {currentOption ? (
                                            <ReactECharts
                                                key={chartMountKey}
                                                notMerge={true}
                                                lazyUpdate={false}
                                                option={currentOption}
                                                style={{ height: "500px", width: "100%" }}
                                                onChartReady={(c) => setChartInstance(c)}
                                            />
                                        ) : (
                                            <p>No data available for this chart.</p>
                                        )}
                                    </Suspense>
                                </div>
                            </div>
                            </Panel>
                        ) : (
                            <>
                                {/* Table */}
                                <Panel title="Member Register" action={<span>{statistics.length} loaded</span>}>
                                <div className="member-stats-modal-table">
                                    {loadingTable && !statistics.length ? <p>Loading…</p> : (
                                        <table>
                                            <thead>
                                            <tr>
                                                <th>Name</th><th>Lodge</th><th>Occupation</th><th>Birthdate</th>
                                                <th>Age at Start</th><th>Age at End</th>
                                                <th>Status</th><th>Type</th><th>Start Date</th><th>End Date</th>
                                                {Array.from({ length: maxDegrees }).map((_, i) => (<th key={i}>Degree {i + 1}</th>))}
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {statistics.map((stat, idx) => (
                                                <tr key={idx}>
                                                    <td>{`${stat.first_name} ${stat.last_name}`}</td>
                                                    <td>{stat.lodge_name || "Unknown"}</td>
                                                    <td>{stat.occupation || "Not Provided"}</td>
                                                    <td>{stat.birthdate ? formatDateToSAST(stat.birthdate) : "Unknown"}</td>
                                                    <td>{stat.age_at_start ?? "N/A"}</td>
                                                    <td>{stat.age_at_end ?? "N/A"}</td>
                                                    <td>{stat.status_name}</td>
                                                    <td>{stat.type_name}</td>
                                                    <td>{formatDateToSAST(stat.start_date)}</td>
                                                    <td>{formatDateToSAST(stat.end_date)}</td>
                                                    {renderDegreeColumns(stat.degrees)}
                                                </tr>
                                            ))}
                                            {!statistics.length && <tr><td colSpan={10 + maxDegrees}>No data available</td></tr>}
                                            </tbody>
                                        </table>
                                    )}
                                    {hasMore && (
                                        <button onClick={loadMore} disabled={loadingTable}>
                                            {loadingTable ? "Loading..." : "Load More"}
                                        </button>
                                    )}
                                </div>
                                </Panel>

                                {/* Pagination */}
                                <div className="member-stats-modal-pagination">
                                    <p>Total Members: {totalMembers}</p>
                                    <button
                                        onClick={() => { if (currentPage > 1) { fetchStatistics(currentPage - 1); setCurrentPage(p=>p-1); } }}
                                        disabled={currentPage === 1 || loadingTable}
                                    >
                                        Previous
                                    </button>
                                    <span>Page {currentPage} of {totalPages}</span>
                                    <button
                                        onClick={() => { if (currentPage < totalPages) { fetchStatistics(currentPage + 1); setCurrentPage(p=>p+1); } }}
                                        disabled={currentPage === totalPages || loadingTable}
                                    >
                                        Next
                                    </button>
                                </div>
                            </>
                        )}

                        <section className="mstat-grid-two">
                            <Panel title="Lodge Rankings" action={<span>{lodgeRankings.length} lodges</span>}>
                                <StatsTable
                                    headers={["#", "Lodge", "Total", "Active", "Deceased", "Resigned"]}
                                    rows={lodgeRankings.map((row, index) => [index + 1, row.lodge, row.total, row.active, row.deceased, row.resigned])}
                                    empty="No lodge data."
                                />
                            </Panel>
                            <Panel title="Records Needing Attention" action={<span>{memberGaps.length} records</span>}>
                                <StatsTable
                                    headers={["Member", "Lodge", "Missing", "Update"]}
                                    rows={memberGaps.slice(0, 40).map((row) => [
                                        `${row.first_name} ${row.last_name}`,
                                        row.lodge_name || "Unknown",
                                        row.missing.join(", "),
                                        <button className="mstat-link-btn" onClick={() => setEditingMemberId(row.member_id)}>Update</button>,
                                    ])}
                                    empty="No gaps for the current filters."
                                />
                            </Panel>
                        </section>
                    </div>
                </div>
                {editingMemberId && (
                    <MemberDetailsModal
                        initialMemberId={editingMemberId}
                        onClose={() => setEditingMemberId(null)}
                    />
                )}
            </div>
        </div>
    );
}

function Kpi({ label, value }) {
    return (
        <div className="mstat-kpi">
            <strong>{value}</strong>
            <span>{label}</span>
        </div>
    );
}

function Panel({ title, action, children }) {
    return (
        <section className="mstat-panel">
            <div className="mstat-panel-heading">
                <h3>{title}</h3>
                {action && <div className="mstat-panel-action">{action}</div>}
            </div>
            {children}
        </section>
    );
}

function StatsTable({ headers, rows, empty }) {
    return (
        <div className="mstat-table-wrap">
            <table className="mstat-table">
                <thead>
                <tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr>
                </thead>
                <tbody>
                {rows.length ? rows.map((row, rowIndex) => (
                    <tr key={`${rowIndex}-${row[0]}`}>
                        {row.map((cell, cellIndex) => <td key={`${rowIndex}-${cellIndex}`}>{cell}</td>)}
                    </tr>
                )) : (
                    <tr>
                        <td colSpan={headers.length}>{empty}</td>
                    </tr>
                )}
                </tbody>
            </table>
        </div>
    );
}

export default MemberStatisticsModal;
